import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from "react";
import { createTheme, Theme, ThemeProvider } from "@mui/material";
import { saveColorTheme } from "../lib/localStorage";

type ThemeContextValue = {
  theme: Theme;
};

export const ThemeContext = createContext<ThemeContextValue>({
  theme: createTheme(),
});

export function ThemesProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(createTheme());

  useEffect(() => {
    const themeResponse = {
      primaryColor: "#218B54", //main
      secondaryColor: "#218B54", //button color
      thirdColor: "#17613A", //button hover color
      fourthColor: "#ffffff", //text on button
      fifthColor: "#D9D9D9", //bg
    };
    let stringTheme = JSON.stringify(themeResponse);
    saveColorTheme(stringTheme);
    setTheme(
      createTheme({
        palette: {
          primary: {
            main: themeResponse.primaryColor, //main
          },
          secondary: {
            main: themeResponse.secondaryColor, //second
          },
          background: {
            default: themeResponse.fifthColor, //fifth
          },
        },
        components: {
          MuiButton: {
            styleOverrides: {
              contained: {
                backgroundColor: themeResponse.secondaryColor, //second
                color: themeResponse.fourthColor, //fourth
                "&:hover": {
                  backgroundColor: themeResponse.thirdColor, //third
                },
              },
              outlined: {
                backgroundColor: "transparent",
                border: "1px solid",
                color: themeResponse.secondaryColor, //second
                "&:hover": {
                  backgroundColor: themeResponse.secondaryColor, //second
                  color: themeResponse.fourthColor, //fourth
                },
              },
              text: {
                "&:hover": {
                  backgroundColor: "white",
                  color: themeResponse.thirdColor, //third
                },
              },
              outlinedSecondary: {
                backgroundColor: "white",
                border: "1px solid",
                color: "#a2a2a2",
                "&:hover": {
                  backgroundColor: "white",
                  color: themeResponse.secondaryColor, //second
                  border: "1px solid " + themeResponse.secondaryColor, //second
                },
                "&:focus": {
                  backgroundColor: "white",
                  color: themeResponse.secondaryColor, //second
                  border: "1px solid " + themeResponse.secondaryColor, //second
                },
                "&:active": {
                  backgroundColor: "white",
                  color: themeResponse.secondaryColor, //second
                  border: "1px solid " + themeResponse.secondaryColor, //second
                },
              },
            },
          },
          MuiInputBase: {
            defaultProps: {
              sx: { borderRadius: "16px", backgroundColor: "#fff" },
            },
          },
        },
      })
    );
  }, []);

  return (
    <ThemeContext.Provider value={{ theme }}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const { theme } = useContext(ThemeContext);
  return { theme };
}
